import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import keyArt from '../assets/branding/key-art.png';

const stats = [
  { label: 'Players', value: '1–4', desc: 'Drop in co-op' },
  { label: 'Missions', value: '∞', desc: 'Procedurally generated' },
  { label: 'Biomes', value: '6', desc: 'Living environments' },
  { label: 'Evolutions', value: '20+', desc: 'Hive counter-tactics' },
];

const phases = [
  {
    icon: '🌿',
    title: 'Exploration',
    desc: 'Drop into a living jungle, uncover ancient ruins, and salvage abandoned outposts before the planet notices you.',
  },
  {
    icon: '⚔️',
    title: 'Combat',
    desc: 'The Hive detects you. Flyers scout, burrowers flank, spitters control the field — every fight forces new tactics.',
  },
  {
    icon: '🧩',
    title: 'Puzzle Dungeons',
    desc: 'Ancient alien temples hide DNA and technology behind crystal beams, gravity chambers, and living locks.',
  },
  {
    icon: '🏃',
    title: 'Extraction',
    desc: 'The objective wakes the Hive. Call your dropship and outrun a collapsing, hostile world in a cinematic sprint.',
  },
];

const features = [
  {
    icon: '🧬',
    title: 'Adaptive Evolution System',
    desc: 'Every mission teaches the Hive. Favor flamethrowers and heat-resistant shells evolve. Lean on snipers and flying assassins appear. Your loadout never stays safe for long.',
  },
  {
    icon: '🪂',
    title: 'Cinematic Orbital Drop',
    desc: 'No loading screens — glide through floating islands, dodge predators, and choose your own landing zone. Land near the Hive for better loot and harder fights.',
  },
  {
    icon: '🐛',
    title: 'Massive Boss Encounters',
    desc: 'Chase a skyscraper-sized burrower on hover bikes, grapple onto a flying leviathan mid-air, or fight a mountain that wakes up beneath you.',
  },
  {
    icon: '🚁',
    title: 'Vehicle Gameplay',
    desc: 'Hover bikes, exo suits, armored walkers, jet wings, and climbing mechs — all destructible, all part of the escape.',
  },
  {
    icon: '🌎',
    title: 'A Living, Breathing Planet',
    desc: 'Trees breathe, rivers change course, bridges grow, and forests relocate overnight. Nothing here is ever truly safe.',
  },
  {
    icon: '🏃‍♂️',
    title: 'Extraction Chase Sequences',
    desc: 'Every mission ends in a procedural, cinematic sprint — collapsing bridges, stampeding creatures, and a forest that fights back.',
  },
];

const biomes = [
  { title: 'Jungle Hive', desc: 'Dense vegetation, ambush predators, living vines.' },
  { title: 'Crystal Caverns', desc: 'Reflective lasers, glowing caves, underground lakes.' },
  { title: 'Floating Mountains', desc: 'Low gravity, flying creatures dominate the sky.' },
  { title: 'Bone Desert', desc: 'Massive skeletons become bridges. Sandworms patrol below.' },
  { title: 'Frozen Hive', desc: 'Ice tunnels, hidden parasites, sudden avalanches.' },
  { title: 'Hive Heart', desc: "The planet's brain — every wall beats like a heart." },
];

const bosses = [
  { title: 'The Titan Burrower', desc: 'A worm the size of a skyscraper. Chase it on hover bikes across collapsing terrain.' },
  { title: 'Sky Leviathan', desc: 'Larger than an aircraft carrier. Grapple onto its body and fight thousands of feet up.' },
  { title: 'The Living Mountain', desc: "Halfway up the climb, it wakes. The fight happens while the mountain moves." },
  { title: 'The Oracle', desc: "The Hive's oldest intelligence. It predicts your habits — you must break them to win." },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function Home() {
  const { user, isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative overflow-hidden border-b border-black/40"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(217,119,6,0.18) 0%, rgba(15,10,5,0) 60%), linear-gradient(180deg, #0b0805 0%, #120c08 55%, #1a1108 100%)',
        }}
      >
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-8 lg:py-24">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-primary)]">
              {isAuthenticated
                ? `${greeting()}, ${user?.name || user?.email || 'Survivor'}`
                : 'Third-Person Action Adventure · Co-op 1–4'}
            </p>
            <h1 className="mt-3 text-4xl font-black leading-none text-white sm:text-5xl lg:text-6xl">
              HIVE<span className="text-[var(--brand-primary)]">FALL</span>
            </h1>
            <p className="mt-4 max-w-lg text-lg font-semibold italic text-amber-100/90">
              "You aren't fighting aliens. You're fighting an entire living planet."
            </p>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-300 sm:text-base">
              Drop into a procedural survival extraction where every mission teaches the Hive
              your tactics — and every escape is a different, cinematic fight for your life.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {isAuthenticated ? (
                <Link to="/profile" className="btn btn-primary">
                  View your profile
                </Link>
              ) : (
                <Link to="/register" className="btn btn-primary">
                  Get started free
                </Link>
              )}
              <Link
                to="/download"
                className="btn"
                style={{ background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.18)' }}
              >
                Download the app
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="text-2xl font-black text-white">{s.value}</p>
                  <p className="text-xs font-bold uppercase tracking-wide text-[var(--brand-primary)]">{s.label}</p>
                  <p className="text-xs text-slate-400">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mx-auto w-full max-w-sm lg:max-w-md">
            <img
              src={keyArt}
              alt="HiveFall key art — a survivor faces a colossal alien hive creature in a glowing jungle"
              className="w-full rounded-[28px] shadow-2xl"
              style={{ boxShadow: '0 30px 80px rgba(217,119,6,0.25), 0 10px 30px rgba(0,0,0,0.5)' }}
            />
          </div>
        </div>
      </section>

      {/* Four Phases */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <span className="pill">The Core Loop</span>
        <h2 className="page-title section-gap">Every Mission, Four Phases</h2>
        <p className="page-subtitle max-w-2xl">
          Drop from orbit, choose your landing zone, and survive a loop that never plays the same way twice.
        </p>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {phases.map((p) => (
            <div key={p.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div
                className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl text-xl"
                style={{ background: 'var(--brand-bg)' }}
              >
                {p.icon}
              </div>
              <p className="font-black text-slate-900">{p.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-slate-200/60" style={{ background: 'var(--brand-bg)' }}>
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <span className="pill">Game Features</span>
          <h2 className="page-title section-gap">Built to Keep You Guessing</h2>
          <p className="page-subtitle max-w-2xl">
            HiveFall isn't a simple shooter — it's a living evolutionary war where exploration, puzzles, and
            heart-pounding escapes make every mission feel new.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 text-3xl">{f.icon}</div>
                <p className="font-black text-slate-900">{f.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Biomes */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <span className="pill">Explore</span>
        <h2 className="page-title section-gap">Six Living Biomes</h2>
        <p className="page-subtitle max-w-2xl">Nothing here is truly dead. The world evolves around you.</p>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {biomes.map((b) => (
            <div
              key={b.title}
              className="rounded-2xl p-6 text-white shadow-sm"
              style={{ background: 'linear-gradient(135deg, #1a1108 0%, #3b2412 100%)' }}
            >
              <p className="font-black text-[var(--brand-primary)]">{b.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Bosses */}
      <section className="border-t border-slate-200/60" style={{ background: 'var(--brand-bg)' }}>
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <span className="pill">Boss Encounters</span>
          <h2 className="page-title section-gap">The Hive Doesn't Fight Fair</h2>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {bosses.map((b) => (
              <div key={b.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <p className="font-black text-slate-900">{b.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section
        className="border-t border-black/40"
        style={{ background: 'linear-gradient(135deg, #1a1108 0%, #0b0805 100%)' }}
      >
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-5 px-4 py-16 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-black text-white sm:text-3xl">Ready to survive the Hive?</h2>
          <p className="max-w-xl text-sm text-slate-300 sm:text-base">
            Get HiveFall on Android and iOS, or create a free account to sync your progress across devices.
          </p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
            <Link to="/download" className="btn btn-primary">
              Download the app
            </Link>
            {!isAuthenticated && (
              <Link
                to="/register"
                className="btn"
                style={{ background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.18)' }}
              >
                Create a free account
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
